import WindowEntity from '@/editor/entities/windowEntity';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { EEntityStatus } from '@/editor/types/entity';
import { globalStores } from '@/editor/stores';
import { EEntityType } from '@/editor/types';
import { OperateAbstract } from './OperateAbstract';
import { IWindowData } from '@/editor/data/model/model';
import { cloneDeep } from 'lodash-es';
import { RulerModel } from '@/editor/entities/2d/rulerModel';
import { calculateWindowDoorRulerInfos } from '@/editor/stores/utils/ruler/windowDoor';
import { WindowAbsorbCalculator } from '@/editor/manager/absorb/window/windowAbsorbCalculator';
import { getWindowAbsorbWallInfos } from '@/editor/manager/absorb/window/utils';

export class OperateDoorWindow extends OperateAbstract<EEntityType.DoorWindow> {
  private _checkedEntity: WindowEntity | null | undefined = null;
  private _checkedWindowData: IWindowData | null = null;

  private _virtualWindowDoorData: IWindowData | null = null;
  private _virtualWindowDoorEntity: WindowEntity | null = null;
  private _ruler = new RulerModel();

  private _adsorbCalculator: WindowAbsorbCalculator;

  constructor() {
    super();

    this._adsorbCalculator = new WindowAbsorbCalculator({ getWallInfos: getWindowAbsorbWallInfos });
  }

  set checkedEntity(entity: WindowEntity | null | undefined) {
    this._checkedEntity = entity;
  }

  get checkedEntity() {
    return this._checkedEntity;
  }

  override startMove(): void {
    if (!this._checkedEntity) return;

    this._checkedWindowData =
      (this._checkedEntity.id && globalStores.modelStore.getWindowData(this._checkedEntity.id)) || null;
    if (!this._checkedWindowData) return;

    this.moving = true;

    this._checkedEntity.setVisible(false);

    // 克隆一份副本
    this._virtualWindowDoorData = cloneDeep(this._checkedWindowData);
    this._virtualWindowDoorEntity = new WindowEntity(this._virtualWindowDoorData);

    this._adsorbCalculator.updateWallData();
  }

  move(point: IPoint2d) {
    this.moving = true;

    if (!this._virtualWindowDoorData) return;

    const virtualWDData = this._virtualWindowDoorData;
    const absorbInfo = this._adsorbCalculator.calculate({
      rawPoint: point,
      margin: virtualWDData.width / 2,
      side: virtualWDData.side
    });

    if (!absorbInfo) {
      virtualWDData.relativeWallInfo = { relativeWallId: '' };
      virtualWDData.center = point;
      this.updateVirturalEntity();

      return;
    }

    let uniformWallData: { id: string; thickness: number };
    if (absorbInfo.wallType === EEntityType.Wall) {
      const wallData = globalStores.wallStore.getWallData(absorbInfo.wallId);
      if (!wallData) return;
      uniformWallData = { id: wallData.id, thickness: wallData.baseData.thickness };
    } else {
      const wallData = globalStores.separateWallStore.getSepData(absorbInfo.wallId, false);
      if (!wallData) return;
      uniformWallData = { id: wallData.id, thickness: wallData.thickness };
    }

    this._virtualWindowDoorData = {
      ...virtualWDData,

      relativeWallInfo: { relativeWallId: uniformWallData.id, wallType: absorbInfo.wallType },
      thickness: uniformWallData.thickness,
      rotation: absorbInfo.rotation,
      center: absorbInfo.absorbPoint,
      distance: absorbInfo.distance
    };
    this.updateVirturalEntity();
  }

  completeMove(_point: IPoint2d) {
    this.moving = false;

    if (this._checkedEntity && this._virtualWindowDoorData) {
      const isValid = !!this._virtualWindowDoorData.relativeWallInfo.relativeWallId;
      if (isValid) {
        globalStores.windowDoorOperation.modifyWindowDoor(this._checkedEntity.id, this._virtualWindowDoorData);
      }
      this._checkedEntity.setVisible(true);
      this._checkedWindowData = globalStores.modelStore.getWindowData(this._checkedEntity.id) || null;
    } else {
      this._checkedWindowData = null;
    }

    this._virtualWindowDoorData = null;
    this.updateVirturalEntity();
  }

  private updateVirturalEntity() {
    if (!this._virtualWindowDoorData) {
      this._virtualWindowDoorEntity?.dispose();
    } else {
      const normal = !!this._virtualWindowDoorData.relativeWallInfo.relativeWallId;
      this._virtualWindowDoorEntity?.update({
        extendedWindowData: this._virtualWindowDoorData,
        status: normal ? EEntityStatus.normal : EEntityStatus.unnormal,
        selected: normal
      });
    }

    this.updateRuler();
  }

  private updateRuler() {
    this._ruler.clear();

    if (!this._checkedEntity || !this._virtualWindowDoorData) return;

    this._ruler.updateModel(calculateWindowDoorRulerInfos(this._virtualWindowDoorData));
  }

  clear() {
    this.checkedEntity = null;
    this._checkedWindowData = null;
    this.moving = false;
    this.pickPoint = null;
    this._virtualWindowDoorData = null;
    this._virtualWindowDoorEntity?.clear();
    this._ruler.clear();
  }

  dispose() {
    this.clear();

    this._virtualWindowDoorEntity?.dispose();
    this._ruler.dispose();
  }
}
